import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
import { __ } from 'stylewhere/i18n'
import { CurrentAlertType } from 'stylewhere/api'
import { BottomRightCorner, CurrentAlerts, DisplayedAlert } from 'stylewhere/shared'
import { Button } from 'stylewhere/components'

const Z_INDEX_BELOW_MODALS = 900
const ENTER_DURATION = 220
const LEAVE_DURATION = 200
const TOGGLE_DURATION = 180
const LIFT_DURATION = 200
const CARD_RADIUS = 12
const DECK_OFFSET = 12
const DECK_GAP = 8
const TOAST_GROUP_ID = 'toast-group:bottom-end'

const ALERT_TYPE_COLORS: Record<CurrentAlertType, string> = {
  ERROR: '#F43C3C',
  WARN: '#FFAD00',
  INFO: '#222222',
  OK: '#18C248'
}

const ALERT_TYPE_LABELS: Record<CurrentAlertType, string> = {
  ERROR: 'alerts.type_error',
  WARN: 'alerts.type_warn',
  INFO: 'alerts.type_info',
  OK: 'alerts.type_ok'
}

// le card dietro sporgono sopra quella in primo piano, sempre più piccole e trasparenti
const BACK_CARD_DEPTHS = [
  { opacity: 0.85, transform: 'translateY(-11px) scale(0.94)' },
  { opacity: 0.6, transform: 'translateY(-22px) scale(0.88)' }
]

const TEXT_COLOR = '#1F1F1F'
const BODY_COLOR = '#333333'

const getColor = (type: CurrentAlertType) => ALERT_TYPE_COLORS[type] ?? ALERT_TYPE_COLORS.INFO

const renderGlyph = (type: CurrentAlertType) => {
  const color = getColor(type)
  // il warning è un triangolo senza cerchio, gli altri un glifo bianco su cerchio pieno
  if (type === 'WARN')
    return (
      <svg width="26" height="26" viewBox="0 0 26 26" aria-hidden>
        <path d="M13 3 L24 22.5 H2 Z" fill={color} stroke={color} strokeWidth="2.5" strokeLinejoin="round" />
        <rect x="11.9" y="9" width="2.2" height="7.5" rx="1.1" fill="#000" />
        <circle cx="13" cy="19.4" r="1.35" fill="#000" />
      </svg>
    )
  return (
    <Glyph style={{ background: color }} aria-hidden>
      <svg width="12" height="12" viewBox="0 0 12 12">
        {type === 'ERROR' && (
          <path d="M2.5 2.5l7 7M9.5 2.5l-7 7" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" />
        )}
        {type === 'OK' && (
          <path
            d="M2 6.3l2.6 2.6L10 3.4"
            stroke="#fff"
            strokeWidth="2.2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
        {type !== 'ERROR' && type !== 'OK' && (
          <>
            <circle cx="6" cy="2.6" r="1.3" fill="#fff" />
            <rect x="4.9" y="4.8" width="2.2" height="5.6" rx="1.1" fill="#fff" />
          </>
        )}
      </svg>
    </Glyph>
  )
}

// i toast Chakra standard sono assoluti e il loro ingombro sta nelle variabili che zag scrive sul toast,
// quelli con render custom (CustomToast) restano nel flusso e allungano il gruppo
const getToastsTop = (group: HTMLElement) => {
  let height = group.offsetHeight
  for (const toast of group.querySelectorAll<HTMLElement>('[data-part="root"][data-state="open"]')) {
    const offset = Number.parseFloat(toast.style.getPropertyValue('--offset')) || 0
    const toastHeight = Number.parseFloat(toast.style.getPropertyValue('--initial-height')) || 0
    height = Math.max(height, offset + toastHeight)
  }
  return height > 0 ? height + Number.parseFloat(getComputedStyle(group).bottom) : 0
}

// osserva i toast solo mentre il deck è visibile
const useToastsTop = (active: boolean) => {
  const [top, setTop] = useState(0)

  useEffect(() => {
    const group = document.getElementById(TOAST_GROUP_ID)
    if (!active || !group) return undefined
    const update = () => setTop(getToastsTop(group))
    update()
    const observer = new MutationObserver(update)
    observer.observe(group, { subtree: true, childList: true, attributeFilter: ['style', 'data-state'] })
    return () => {
      observer.disconnect()
      setTop(0)
    }
  }, [active])

  return top
}

const renderBackCard = (alert: DisplayedAlert, depth: number) => (
  <BackCard key={alert.version} style={BACK_CARD_DEPTHS[depth]} data-testid="current-alert-back-card">
    <Strip style={{ background: getColor(alert.type) }} />
  </BackCard>
)

const CurrentAlertCard = ({ alert }: { alert: DisplayedAlert }) => {
  const [dontShowAgain, setDontShowAgain] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const leaveTimer = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => () => clearTimeout(leaveTimer.current), [])

  // la card esce scivolando in basso e solo dopo si chiude l'alert; un secondo click è ignorato
  const onOk = () => {
    if (leaving) return
    setLeaving(true)
    leaveTimer.current = setTimeout(() => CurrentAlerts.dismiss(alert, dontShowAgain), LEAVE_DURATION)
  }

  return (
    <FrontCard data-leaving={leaving || undefined} data-testid="current-alert">
      <Clip>
        <Strip style={{ background: getColor(alert.type) }} />
        <Content>
          <Header>
            {renderGlyph(alert.type)}
            <TypeLabel>{__(ALERT_TYPE_LABELS[alert.type] ?? ALERT_TYPE_LABELS.INFO)}</TypeLabel>
          </Header>
          <Title>{alert.title}</Title>
          {alert.body && <Body>{alert.body}</Body>}
          <Footer>
            <Toggle
              type="button"
              role="switch"
              aria-checked={dontShowAgain}
              onClick={() => setDontShowAgain((v) => !v)}
            >
              <Track data-on={dontShowAgain || undefined}>
                <Knob />
              </Track>
              <span>{__('alerts.dont_show_again')}</span>
            </Toggle>
            <Button
              title={__('alerts.ok')}
              variant="primary"
              size="small"
              radius={100}
              padding="0 28px"
              style={{ height: 44, fontSize: 15, fontWeight: 500, letterSpacing: 0.2, textTransform: 'uppercase' }}
              onClick={onOk}
            />
          </Footer>
        </Content>
      </Clip>
    </FrontCard>
  )
}

// Mazzo di card come sull'handheld: l'alert più grave davanti, al massimo due card dietro.
// A differenza dell'handheld non è modale: sta in basso a destra e il resto della pagina resta usabile,
// sotto le modali dell'app così non copre un dialogo aperto dall'operatore.
export const CurrentAlertsPanel = () => {
  const alerts = useSyncExternalStore(CurrentAlerts.subscribe, CurrentAlerts.getSnapshot)
  const cornerTop = useSyncExternalStore(BottomRightCorner.subscribe, BottomRightCorner.getTop)
  const toastsTop = useToastsTop(alerts.length > 0)
  if (alerts.length === 0) return null

  // se toast o altri elementi fissi occupano l'angolo, il deck si alza sopra di loro
  const lift = Math.max(0, Math.max(cornerTop, toastsTop) + DECK_GAP - DECK_OFFSET)

  const [front, ...queued] = alerts
  const behind = queued.slice(0, BACK_CARD_DEPTHS.length)

  return (
    <Deck role="status" data-testid="current-alerts" style={lift ? { transform: `translateY(-${lift}px)` } : undefined}>
      {behind.map((alert, depth) => renderBackCard(alert, depth)).reverse()}
      <CurrentAlertCard key={front.version} alert={front} />
    </Deck>
  )
}

const enterBack = keyframes`
  from { opacity: 0; transform: translateY(-33px) scale(0.82); }
`

const enter = keyframes`
  from { opacity: 0.6; transform: translateY(-11px) scale(0.94); }
  to { opacity: 1; transform: none; }
`

const Deck = styled.div`
  position: fixed;
  right: ${DECK_OFFSET}px;
  bottom: ${DECK_OFFSET}px;
  z-index: ${Z_INDEX_BELOW_MODALS};
  width: min(380px, calc(100vw - ${DECK_OFFSET * 2}px));
  transition: transform ${LIFT_DURATION}ms ease-out;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const Card = styled.div`
  background: #fff;
  border-radius: ${CARD_RADIUS}px;
  transform-origin: top center;
`

// quando la card davanti esce le altre avanzano di un posto; quella che entra in fondo al mazzo
// arriva da dietro (lo stato finale è lo style inline della sua profondità)
const BackCard = styled(Card)`
  position: absolute;
  inset: 0;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  animation: ${enterBack} ${ENTER_DURATION}ms ease-out;
  transition:
    opacity ${ENTER_DURATION}ms ease-out,
    transform ${ENTER_DURATION}ms ease-out;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`

const FrontCard = styled(Card)`
  position: relative;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
  animation: ${enter} ${ENTER_DURATION}ms ease-out;
  transition:
    opacity ${LEAVE_DURATION}ms ease-out,
    transform ${LEAVE_DURATION}ms ease-out;
  &[data-leaving] {
    opacity: 0;
    transform: translateY(28px);
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`

// il ritaglio sta su un elemento interno: sulla card taglierebbe l'ombra
const Clip = styled.div`
  border-radius: ${CARD_RADIUS}px;
  overflow: hidden;
`

const Strip = styled.div`
  height: 5px;
`

const Content = styled.div`
  padding: 18px 20px 20px;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
`

const Glyph = styled.div`
  flex: none;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`

const TypeLabel = styled.span`
  flex: 1;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${TEXT_COLOR};
`

const Title = styled.div`
  margin-top: 14px;
  font-size: 19px;
  font-weight: 700;
  line-height: 24px;
  color: ${TEXT_COLOR};
  text-wrap: pretty;
`

const Body = styled.div`
  margin-top: 8px;
  max-height: 236px;
  overflow: auto;
  font-size: 15px;
  font-weight: 300;
  line-height: 21px;
  color: ${BODY_COLOR};
  white-space: pre-line;
  text-wrap: pretty;
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  padding-top: 10px;
  border-top: 0.5px solid #c8c7cc;
`

const Toggle = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  font-size: 14px;
  font-weight: 300;
  color: ${BODY_COLOR};
`

const Track = styled.span`
  flex: none;
  position: relative;
  width: 42px;
  height: 26px;
  border-radius: 100px;
  background: #e3e3e3;
  transition: background ${TOGGLE_DURATION}ms ease-out;
  &[data-on] {
    background: #18c248;
  }
`

const Knob = styled.span`
  position: absolute;
  top: 2px;
  left: 2px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  transition: transform ${TOGGLE_DURATION}ms ease-out;
  [data-on] > & {
    transform: translateX(16px);
  }
`
